import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints, urlSheetsTemplate } from "../config/api";

const emptyPromoteForm = {
  sheetId: "",
  name: "",
  description: "",
  organization_id: "",
  is_published: true,
};

const SheetsTemplates = ({ onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [promoteForm, setPromoteForm] = useState(emptyPromoteForm);
  const [promoting, setPromoting] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const authHeaders = () => {
    const headers = getAuthHeaders();
    return {
      ...headers,
      Accept: "*/*",
      "Content-Type": "application/json",
    };
  };

  const fetchTemplates = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) {
      setError("Please log in to manage sheet templates.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(endpoints.sheetsTemplates, {
        headers: authHeaders(),
      });
      const list = Array.isArray(response.data?.templates)
        ? response.data.templates
        : Array.isArray(response.data)
          ? response.data
          : [];
      setTemplates(list);
    } catch (err) {
      console.error("Error fetching sheet templates", err);
      setError(getErrorMessage(err, "Failed to fetch sheet templates."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await axios.get(endpoints.orgs, {
          headers: authHeaders(),
        });
        setOrgs(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching orgs", err);
      }
    };
    fetchOrgs();
  }, []);

  const updatePromoteField = (field, value) => {
    setPromoteForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePromote = async (event) => {
    event.preventDefault();
    const sheetId = String(promoteForm.sheetId || "").trim();
    const name = String(promoteForm.name || "").trim();
    if (!/^\d+$/.test(sheetId)) {
      setError("Sheet number must be a numeric sheet id.");
      return;
    }
    if (!name) {
      setError("Template name is required.");
      return;
    }
    if (!getAuthHeaders().Authorization) {
      setError("Please log in to promote a sheet.");
      return;
    }

    setPromoting(true);
    setError("");
    setSuccess("");
    try {
      const body = {
        sheetId,
        name,
        is_published: Boolean(promoteForm.is_published),
      };
      const description = String(promoteForm.description || "").trim();
      if (description) body.description = description;

      const orgRaw = String(promoteForm.organization_id || "").trim();
      if (orgRaw !== "") {
        const orgId = Number(orgRaw);
        if (!Number.isInteger(orgId)) {
          setError("Organization must be a valid id, or leave blank for all users.");
          setPromoting(false);
          return;
        }
        body.organization_id = orgId;
      }

      const response = await axios.post(endpoints.sheetsPromoteToTemplate, body, {
        headers: authHeaders(),
      });
      const created = response.data?.template;
      const scope =
        body.organization_id == null
          ? "all users (global default)"
          : `organization ${body.organization_id}`;
      setSuccess(
        `Template “${created?.name || name}” (#${created?.id ?? "—"}) created from sheet ${sheetId} for ${scope}.`
      );
      setPromoteForm(emptyPromoteForm);
      await fetchTemplates();
    } catch (err) {
      console.error("Error promoting sheet to template", err);
      setError(getErrorMessage(err, "Failed to promote sheet to template."));
    } finally {
      setPromoting(false);
    }
  };

  const openDetails = async (templateId) => {
    setDetailsLoading(true);
    setError("");
    setSelectedTemplate(null);
    try {
      const response = await axios.get(urlSheetsTemplate(templateId), {
        headers: authHeaders(),
      });
      setSelectedTemplate(response.data?.template ?? response.data);
    } catch (err) {
      console.error("Error fetching template details", err);
      setError(getErrorMessage(err, "Failed to load template details."));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDelete = async (template) => {
    const label = `“${template.name}” (#${template.id})`;
    if (!window.confirm(`Delete template ${label}? This cannot be undone.`)) {
      return;
    }
    setDeletingId(template.id);
    setError("");
    setSuccess("");
    try {
      await axios.delete(urlSheetsTemplate(template.id), {
        headers: authHeaders(),
      });
      setSuccess(`Deleted template ${label}.`);
      if (selectedTemplate?.id === template.id) setSelectedTemplate(null);
      await fetchTemplates();
    } catch (err) {
      console.error("Error deleting template", err);
      setError(getErrorMessage(err, "Failed to delete template."));
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
  };

  const orgLabel = (organizationId) => {
    if (organizationId == null) return "All users";
    const org = orgs.find(
      (o) => String(o.id ?? o.organizationId ?? o.organization_id) === String(organizationId)
    );
    const name = org?.name ?? org?.orgName ?? org?.organization_name;
    return name ? `${name} (${organizationId})` : String(organizationId);
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.title}>Sheet Templates</h2>
          <p style={styles.subtitle}>
            Promote any sheet by id into a reusable template, optionally scoped to one
            organization. Leave organization blank to make it available to all users as a
            global default template.
          </p>
        </div>
        <button type="button" style={styles.closeButton} onClick={onClose}>
          Close
        </button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}
      {success && <div style={styles.successBox}>{success}</div>}

      <section style={styles.card}>
        <h3 style={styles.sectionTitle}>Set template from sheet number</h3>
        <p style={styles.hint}>
          Enter an existing sheet id. The workbook snapshot is captured and stored as a
          template users can instantiate.
        </p>
        <form onSubmit={handlePromote} style={styles.form}>
          <label style={styles.label}>
            Sheet number (id)
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={promoteForm.sheetId}
              onChange={(e) => updatePromoteField("sheetId", e.target.value)}
              placeholder="e.g. 42"
              style={styles.input}
              required
            />
          </label>
          <label style={styles.label}>
            Template name
            <input
              type="text"
              value={promoteForm.name}
              onChange={(e) => updatePromoteField("name", e.target.value)}
              placeholder="Display name for users"
              style={styles.input}
              required
            />
          </label>
          <label style={styles.label}>
            Description (optional)
            <textarea
              value={promoteForm.description}
              onChange={(e) => updatePromoteField("description", e.target.value)}
              placeholder="Short description"
              style={{ ...styles.input, minHeight: 72, resize: "vertical" }}
              rows={3}
            />
          </label>
          <label style={styles.label}>
            Organization scope
            <select
              value={promoteForm.organization_id}
              onChange={(e) => updatePromoteField("organization_id", e.target.value)}
              style={styles.select}
            >
              <option value="">All users (global default)</option>
              {orgs.map((org) => {
                const id = org.id ?? org.organizationId ?? org.organization_id;
                const name = org.name ?? org.orgName ?? org.organization_name ?? `Org ${id}`;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={promoteForm.is_published}
              onChange={(e) => updatePromoteField("is_published", e.target.checked)}
            />
            Published (visible to users)
          </label>
          <button type="submit" style={styles.primaryButton} disabled={promoting}>
            {promoting ? "Promoting…" : "Promote sheet to template"}
          </button>
        </form>
      </section>

      <section style={styles.card}>
        <div style={styles.listHeader}>
          <h3 style={styles.sectionTitle}>Existing templates</h3>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={fetchTemplates}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {loading ? (
          <p style={styles.muted}>Loading templates…</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Scope</th>
                  <th style={styles.th}>Updated</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ ...styles.td, color: "#6b7280", textAlign: "center" }}>
                      No published templates found.
                    </td>
                  </tr>
                ) : (
                  templates.map((template) => (
                    <tr key={template.id}>
                      <td style={styles.td}>{template.id}</td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 600 }}>{template.name}</div>
                        {template.description && (
                          <div style={styles.desc}>{template.description}</div>
                        )}
                        {template.organization_id == null && (
                          <span style={styles.badge}>Global default</span>
                        )}
                      </td>
                      <td style={styles.td}>{orgLabel(template.organization_id)}</td>
                      <td style={styles.td}>{formatDate(template.updated_at)}</td>
                      <td style={{ ...styles.td, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          style={styles.linkButton}
                          onClick={() => openDetails(template.id)}
                          disabled={detailsLoading}
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          style={styles.dangerButton}
                          onClick={() => handleDelete(template)}
                          disabled={deletingId === template.id}
                        >
                          {deletingId === template.id ? "Deleting…" : "Delete"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {(detailsLoading || selectedTemplate) && (
        <div style={styles.modalOverlay} onClick={() => setSelectedTemplate(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            {detailsLoading || !selectedTemplate ? (
              <p style={styles.muted}>Loading details…</p>
            ) : (
              <>
                <h3 style={styles.sectionTitle}>{selectedTemplate.name}</h3>
                <dl style={styles.dl}>
                  <div style={styles.dlRow}>
                    <dt style={styles.dt}>ID</dt>
                    <dd style={styles.dd}>{selectedTemplate.id}</dd>
                  </div>
                  <div style={styles.dlRow}>
                    <dt style={styles.dt}>Description</dt>
                    <dd style={styles.dd}>{selectedTemplate.description || "—"}</dd>
                  </div>
                  <div style={styles.dlRow}>
                    <dt style={styles.dt}>Published</dt>
                    <dd style={styles.dd}>
                      {selectedTemplate.is_published == null
                        ? "—"
                        : selectedTemplate.is_published
                          ? "Yes"
                          : "No"}
                    </dd>
                  </div>
                  <div style={styles.dlRow}>
                    <dt style={styles.dt}>Scope</dt>
                    <dd style={styles.dd}>{orgLabel(selectedTemplate.organization_id)}</dd>
                  </div>
                  <div style={styles.dlRow}>
                    <dt style={styles.dt}>Active tab</dt>
                    <dd style={styles.dd}>
                      {selectedTemplate.active_sheet_name || "—"}
                    </dd>
                  </div>
                  <div style={styles.dlRow}>
                    <dt style={styles.dt}>Created</dt>
                    <dd style={styles.dd}>{formatDate(selectedTemplate.created_at)}</dd>
                  </div>
                  <div style={styles.dlRow}>
                    <dt style={styles.dt}>Updated</dt>
                    <dd style={styles.dd}>{formatDate(selectedTemplate.updated_at)}</dd>
                  </div>
                </dl>
                {Array.isArray(selectedTemplate.tabs) && selectedTemplate.tabs.length > 0 && (
                  <>
                    <h4 style={styles.tabsTitle}>Tabs</h4>
                    <ul style={styles.tabsList}>
                      {selectedTemplate.tabs.map((tab) => (
                        <li key={tab.name} style={styles.tabItem}>
                          <strong>{tab.name}</strong>
                          {tab.active ? " (active)" : ""}
                          {tab.cell_count != null ? ` — ${tab.cell_count} cells` : ""}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <div style={styles.modalActions}>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => setSelectedTemplate(null)}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    width: "100%",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "700",
    color: "#3730a3",
  },
  subtitle: {
    margin: "8px 0 0",
    fontSize: "14px",
    lineHeight: 1.5,
    color: "#64748b",
    maxWidth: "640px",
  },
  closeButton: {
    padding: "10px 20px",
    fontSize: "14px",
    backgroundColor: "#3730a3",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    flexShrink: 0,
  },
  errorBox: {
    color: "#b91c1c",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#fee2e2",
    borderRadius: "6px",
    border: "1px solid #ef4444",
  },
  successBox: {
    color: "#047857",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#d1fae5",
    borderRadius: "6px",
    border: "1px solid #10b981",
  },
  card: {
    width: "100%",
    padding: "20px",
    marginBottom: "20px",
    borderRadius: "8px",
    border: "1px solid #c7d2fe",
    backgroundColor: "#fafbff",
    boxSizing: "border-box",
  },
  sectionTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e1b4b",
  },
  hint: {
    margin: "8px 0 16px",
    fontSize: "13px",
    color: "#64748b",
    lineHeight: 1.45,
  },
  form: {
    display: "grid",
    gap: "14px",
    maxWidth: "520px",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    boxSizing: "border-box",
    color: "#1f2937",
    outline: "none",
    fontWeight: "400",
  },
  select: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    boxSizing: "border-box",
    color: "#1f2937",
    outline: "none",
    cursor: "pointer",
    fontWeight: "400",
  },
  primaryButton: {
    padding: "12px 18px",
    fontSize: "14px",
    backgroundColor: "#3730a3",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    width: "fit-content",
  },
  secondaryButton: {
    padding: "8px 14px",
    fontSize: "13px",
    backgroundColor: "#e0e7ff",
    color: "#3730a3",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  },
  linkButton: {
    padding: "6px 12px",
    fontSize: "13px",
    backgroundColor: "#3730a3",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    marginRight: "8px",
  },
  dangerButton: {
    padding: "6px 12px",
    fontSize: "13px",
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    border: "1px solid #fca5a5",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  },
  listHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "14px",
    gap: "12px",
  },
  muted: {
    color: "#6b7280",
    fontSize: "14px",
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#fff",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    backgroundColor: "#f3f4f6",
    fontWeight: "600",
    color: "#374151",
    borderBottom: "2px solid #e5e7eb",
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1f2937",
    verticalAlign: "top",
  },
  desc: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "400",
  },
  badge: {
    display: "inline-block",
    marginTop: "6px",
    padding: "2px 8px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#047857",
    backgroundColor: "#d1fae5",
    borderRadius: "999px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "16px",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "24px",
    maxWidth: "520px",
    width: "100%",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
  dl: {
    margin: "16px 0 0",
  },
  dlRow: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    gap: "8px",
    padding: "8px 0",
    borderBottom: "1px solid #f3f4f6",
  },
  dt: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#6b7280",
  },
  dd: {
    margin: 0,
    fontSize: "14px",
    color: "#1f2937",
  },
  tabsTitle: {
    margin: "16px 0 8px",
    fontSize: "14px",
    fontWeight: "700",
    color: "#1e1b4b",
  },
  tabsList: {
    margin: 0,
    paddingLeft: "18px",
  },
  tabItem: {
    fontSize: "13px",
    color: "#374151",
    marginBottom: "4px",
  },
  modalActions: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "flex-end",
  },
};

export default SheetsTemplates;
